/**
 * useBulkAnnotationActions Hook
 * 
 * Manages bulk operations on annotations including:
 * - Bulk approve (mark as reviewed)
 * - Bulk delete
 * - Progress tracking
 * - Batch processing to avoid overwhelming the server
 */
import { useCallback } from 'react';

const useBulkAnnotationActions = ({
  bulkSelectedItems,
  ExamSessionStore,
  AppStore,
  setBulkChangeType,
  setBulkChangeInProcess,
  setBulkSelectedItemsCompleted,
  setBulkChangePercentage,
  setBulkEdit,
  setBulkChangeCompleted,
  setBulkSelectedItems,
  setSelectAll,
}) => {
  /**
   * Handle bulk change operations (approve/delete)
   * Processes annotations in batches to avoid overwhelming the server
   */
  const handleBulkChange = useCallback(async ({ changeType }) => {
    setBulkChangeType(changeType);
    setBulkChangeInProcess(true);
    setBulkSelectedItemsCompleted([]);
    setBulkChangePercentage(0);
    setBulkEdit(false);
    setBulkChangeCompleted(false);

    try {
      // Process in batches of 7 to avoid overwhelming the server
      const batchSize = 7;
      const batches = [];

      // Split bulkSelectedItems into chunks
      for (let i = 0; i < bulkSelectedItems.length; i += batchSize) {
        batches.push(bulkSelectedItems.slice(i, i + batchSize));
      }

      // Process each batch sequentially
      let completedCount = 0;
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const updatePromises = batch.map(annotationId => {
          if (changeType === "approve") {
            const changePayload = {};
            changePayload.reviewedBy = AppStore.user.username;
            changePayload.reviewedDate = new Date().toISOString();
            changePayload.updatedBy = AppStore.user.username;
            changePayload.updatedDate = new Date().toISOString();
            return ExamSessionStore.updateAnnotation(annotationId, changePayload);
          } else if (changeType === "delete") {
            return ExamSessionStore.deleteAnnotation(annotationId);
          }
        });

        // Wait for this batch to complete before starting the next
        await Promise.all(updatePromises);

        // Update progress based on completed batches
        completedCount += batch.length;
        setBulkSelectedItemsCompleted(prev => [...prev, ...batch]);
        setBulkChangePercentage(Math.round((completedCount / bulkSelectedItems.length) * 100));
      }
      await ExamSessionStore.loadAnnotations();
    } catch (error) {
      console.error('Error during bulk change:', error);
    } finally {
      setBulkEdit(false);
      setBulkSelectedItems([]);
      setSelectAll(false);
      setBulkChangeInProcess(false);
      setBulkChangeCompleted(true);
    }
  }, [bulkSelectedItems, ExamSessionStore, AppStore, setBulkChangeType, setBulkChangeInProcess, setBulkSelectedItemsCompleted, setBulkChangePercentage, setBulkEdit, setBulkChangeCompleted, setBulkSelectedItems, setSelectAll]);

  const handleBulkApprove = useCallback(async () => {
    return await handleBulkChange({ changeType: "approve" });
  }, [handleBulkChange]);

  const handleBulkDelete = useCallback(async () => {
    return await handleBulkChange({ changeType: "delete" });
  }, [handleBulkChange]);

  return {
    handleBulkApprove,
    handleBulkDelete,
  };
};

export default useBulkAnnotationActions;
